import { useEffect, useMemo, useState } from "react";
import { useClerk } from "@clerk/react";
import { useLocation } from "wouter";
import { useDemoMode } from "@/components/DemoBanner";
import { AlertTriangle, Clock } from "lucide-react";
import { readSalesPresenterCookie } from "@/lib/salesPresenter";

const SESSION_MAX_MS = 15 * 60 * 1000;
const WARN_AT_MS = 13 * 60 * 1000;
const COOKIE = "ah_demo_started_at";

function readStartedAt(): number | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${COOKIE}=`));
  if (!match) return null;
  const raw = decodeURIComponent(match.split("=")[1] ?? "");
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : null;
}

function fmtRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * 15-minute demo session timer. Shows a small clock badge from the moment the
 * session starts, swaps to a destructive warning at the 13-minute mark
 * ("2 minutes left — sign up to keep your data"), and signs the visitor out
 * + bounces them to /sign-up at the 15-minute mark.
 *
 * The session-start timestamp is set by the server in the `ah_demo_started_at`
 * cookie when /api/demo/verify succeeds. If the cookie is missing (e.g. the
 * visitor reloaded after expiry), we treat that as expired immediately.
 */
export function DemoSessionTimer() {
  const isDemo = useDemoMode();
  const clerk = useClerk();
  const [, setLocation] = useLocation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isDemo) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isDemo]);

  // Sales-presenter mode (task #485): a rep is driving a live demo and the
  // 15-min countdown would be a distraction. The non-httpOnly mirror cookie
  // lets us decide client-side; the server still enforces watermark / gate.
  const presenter = useMemo(() => (isDemo ? readSalesPresenterCookie() : { active: false, expiresAt: null }), [isDemo]);
  const startedAt = useMemo(
    () => (isDemo && !presenter.active ? readStartedAt() : null),
    [isDemo, presenter.active],
  );
  const elapsed = startedAt ? now - startedAt : null;
  const remaining = elapsed != null ? SESSION_MAX_MS - elapsed : null;

  useEffect(() => {
    if (!isDemo) return;
    if (remaining != null && remaining <= 0) {
      // Best-effort sign-out then redirect; even if signOut throws, the
      // redirect still lands them on /sign-up where Clerk will tear down.
      void clerk.signOut().catch(() => undefined).finally(() => {
        setLocation("/demo?expired=1", { replace: true });
      });
    }
  }, [isDemo, remaining, clerk, setLocation]);

  if (!isDemo || remaining == null) return null;
  if (remaining <= 0) return null;

  const warning = remaining <= SESSION_MAX_MS - WARN_AT_MS; // last 2 minutes
  return (
    <div
      data-testid="demo-session-timer"
      className={
        "sticky top-0 z-40 flex items-center gap-2 px-4 sm:px-6 py-1.5 text-xs font-medium border-b " +
        (warning
          ? "bg-amber-100 text-amber-900 border-amber-200"
          : "bg-indigo-50 text-indigo-700 border-indigo-100")
      }
    >
      {warning ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
      <span>
        {warning
          ? `Demo session ends in ${fmtRemaining(remaining)} — sign up to keep your data`
          : `Demo session: ${fmtRemaining(remaining)} remaining`}
      </span>
    </div>
  );
}
